/**
 * @file RedirectDialog.tsx
 * @description Окно «Добавить переадресацию» / «Изменить переадресацию»
 * (phase19.16, партия Р3в, v0.19.64; SB131; uiux-r3 §3.5, db-design-r3v §4).
 *
 * «Откуда» — адрес на этом сайте, начинается с «/»; подсказки запретов
 * (служебный адрес, раздел) — сразу по таблице маршрутов shared, остальное
 * («с адреса открывается живая страница», «получится круг») — ответом
 * сервера под нужным полем. «Куда» — `RedirectTargetField`. Правка
 * автоматического правила делает его ручным — об этом строка в окне.
 * Предупреждения сервера (скрытая цель, «осталось N правил») — тостом.
 * «С этого адреса уже есть переадресация» — кнопка «Изменить существующую»
 * открывает её правку (`details.existing`).
 * До sm — на весь экран, кнопки закреплены снизу.
 *
 * «SEO-техника» v0.19.65 (SB148, uiux-seo-tech §3.1): из журнала «Не
 * найдено» окно открывается с заполненным «откуда» (`initialFrom`, поле
 * доступно для правки) и фокусом на «Куда».
 */

import { useEffect, useId, useRef, useState } from "react";

import { Button } from "../../ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../../ui/dialog";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { NoticeStrip } from "../../ui/NoticeStrip";
import { useNotify } from "../../ui/NotifyProvider";
import { RedirectTargetField } from "./RedirectTargetField";
import { siteRedirects, type SiteRedirectItem } from "../../../lib/api-site-redirects";
import { EMPTY_REDIRECT_TARGET, redirectTargetInput, redirectTargetOf, redirectTargetProblem, type RedirectTargetValue } from "../../../lib/site-seo/redirect-target";
import { fromPathNote, fromPathProblem, isSelfRedirect } from "../../../lib/site-seo/redirect-view";
import { REDIRECT_WARNING_TEXT, redirectErrorField, seoErrorText, takenRedirect } from "../../../lib/site-seo/seo-errors";

interface Props {
  open: boolean;
  /** Правимая строка; `null` — новая переадресация. */
  editing: SiteRedirectItem | null;
  /** «Откуда» новой переадресации (строка журнала «Не найдено»); фокус — на «Куда». */
  initialFrom?: string | null;
  onClose: () => void;
  /** Сохранено — перечитать список. */
  onSaved: () => void;
  /** Открыть правку уже существующего правила с тем же «откуда». */
  onEditExisting: (item: SiteRedirectItem) => void;
}

interface Errors {
  from?: string | null;
  target?: string | null;
  common?: string | null;
}

export function RedirectDialog({ open, editing, initialFrom = null, onClose, onSaved, onEditExisting }: Props) {
  const notify = useNotify();
  const fromId = useId();
  const [from, setFrom] = useState("");
  const [target, setTarget] = useState<RedirectTargetValue>(EMPTY_REDIRECT_TARGET);
  const [errors, setErrors] = useState<Errors>({});
  const [busy, setBusy] = useState(false);
  const [existing, setExisting] = useState<SiteRedirectItem | null>(null);
  // Поле «Куда» — для начального фокуса, когда «откуда» уже заполнено.
  const targetBox = useRef<HTMLDivElement>(null);
  const focusTarget = !editing && !!initialFrom;

  useEffect(() => {
    if (!open) return;
    setFrom(editing?.fromDisplay ?? initialFrom ?? "");
    setTarget(editing ? redirectTargetOf(editing) : EMPTY_REDIRECT_TARGET);
    setErrors({});
    setExisting(null);
  }, [open, editing, initialFrom]);

  async function submit() {
    const found: Errors = { from: fromPathProblem(from), target: redirectTargetProblem(target) };
    if (!found.target && target.kind === "PATH" && isSelfRedirect(from, target.path)) found.target = "Адрес «откуда» и «куда» совпадают.";
    setErrors(found);
    const input = redirectTargetInput(target);
    if (found.from || found.target || !input) return;
    setBusy(true);
    try {
      const body = { fromPath: from.trim(), ...input };
      const res = editing ? await siteRedirects.update(editing.id, body) : await siteRedirects.create(body);
      notify.success(editing ? "Переадресация изменена — заработает в течение минуты." : "Переадресация добавлена — заработает в течение минуты.");
      for (const code of res.warnings) notify.warning(REDIRECT_WARNING_TEXT[code] ?? code);
      onSaved();
      onClose();
    } catch (err) {
      setExisting(takenRedirect<SiteRedirectItem>(err));
      const field = redirectErrorField(err);
      const text = seoErrorText(err, "Не удалось сохранить переадресацию.");
      setErrors(field ? { [field]: text } : { common: text });
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={(next) => (!next && !busy ? onClose() : null)}>
      <DialogContent
        fullScreen
        className="sm:max-h-[90vh] sm:max-w-[35rem] sm:overflow-y-auto"
        initialFocus={focusTarget ? () => targetBox.current?.querySelector<HTMLElement>("button, input") ?? true : undefined}
      >
        <DialogHeader>
          <DialogTitle>{editing ? "Изменить переадресацию" : "Добавить переадресацию"}</DialogTitle>
          <DialogDescription>Со старого адреса покупатель и поисковик попадут на новый (код 301).</DialogDescription>
        </DialogHeader>
        {editing?.source === "AUTO" ? (
          <p className="type-caption text-on-surface-variant">Переадресация появилась сама при смене адреса. После правки она станет ручной и войдёт в предел 1000 правил.</p>
        ) : null}
        {errors.common ? <NoticeStrip tone="error">{errors.common}</NoticeStrip> : null}
        <div className="flex min-w-0 flex-col gap-1">
          <Label htmlFor={fromId}>Откуда</Label>
          <Input
            id={fromId}
            value={from}
            inputMode="url"
            placeholder="/old-page"
            disabled={busy}
            aria-invalid={!!errors.from}
            onChange={(e) => setFrom(e.target.value)}
            onBlur={() => from.trim() && setErrors((prev) => ({ ...prev, from: fromPathProblem(from) }))}
          />
          {errors.from ? (
            <div className="flex flex-col items-start gap-1">
              <p className="type-caption text-destructive">{errors.from}</p>
              {existing ? (
                <Button variant="outline" className="max-sm:w-full" onClick={() => onEditExisting(existing)}>
                  Изменить существующую
                </Button>
              ) : null}
            </div>
          ) : (
            <p className="type-caption text-on-surface-variant">
              {fromPathNote(from) ?? "Адрес на этом сайте, начинается с «/». Регистр букв не важен, ?utm перенесутся сами."}
            </p>
          )}
        </div>
        <div ref={targetBox} className="min-w-0">
          <RedirectTargetField value={target} onChange={setTarget} error={errors.target} disabled={busy} />
        </div>
        <DialogFooter className="sticky -bottom-6 z-10 -mx-6 -mb-6 max-sm:mt-auto bg-surface-lowest px-6 py-3 [box-shadow:0_-1px_0_var(--outline-variant)] flex-row justify-end">
          <Button variant="ghost" className="max-sm:flex-1" onClick={onClose} disabled={busy}>
            Отмена
          </Button>
          <Button className="max-sm:flex-1" loading={busy} onClick={() => void submit()}>
            {editing ? "Сохранить" : "Добавить"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
